import * as React from "react";
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {
  DealerLocation,
  SetupType,
  PlatformFeature,
} from "../../../models/features/Integration/DmsSetup";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { useDispatch } from "react-redux";
import {
  addLocation,
  addMappedLocations,
  removeLocation,
  setTimezone,
  useGetStoreLocationsMutation,
  useGetTimezonesQuery,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { hasPlatformFeatureAccess } from "../../../services/featurePermissions/featureMatrix";
import {
  SubscriptionFeatureName,
  useHasSubscriptionFeature,
} from "../../../services/featurePermissions/subscriptionFeatures";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import AppConfirmDialog from "../../app/AppConfirmDialog";
import { setupTypeLabelMap } from "../../../services/utilities/utility";

interface DmsLocationsProps {
  locations: DealerLocation[];
  handleNext: any;
  handleBack: any;
}

const DmsLocations: React.FunctionComponent<DmsLocationsProps> = ({
  locations,
  handleBack,
  handleNext,
}) => {
  const { data: timezonesResponse, isLoading: isTimezonesLoading } =
    useGetTimezonesQuery();
  const timezoneOptions: any[] = timezonesResponse?.entityList || [];

  const dispatch = useDispatch();
  const hasMultiLocation = useHasSubscriptionFeature(
    SubscriptionFeatureName.MultiLocation,
  );
  const {
    storeAccessToken: shopifyAccessToken,
    storeUrl: shopifyUrl,
    setupType,
    siteCredentials,
    timezoneId,
  } = useSelector((state: AppState) => state.dmsReducer);
  const locationIds = useSelector(
    (state: AppState) => state.dmsReducer.locationIds,
  );
  const [storeLocations, setStoreLocations] = React.useState<
    { id: number; name: string; address: string }[]
  >([]);
  const [dmsLocations, setDmsLocations] =
    React.useState<DealerLocation[]>(locations);
  const [locationToMap, setLocationToMap] = React.useState<DealerLocation>();
  const [selectedStoreLocationId, setSelectedStoreLocationId] =
    React.useState<number>(0);
  const [openMapper, setOpenMapper] = React.useState<boolean>(false);
  const [navigateOpen, setNavigateOpen] = React.useState(false);

  const [storeLocationMutation, storeLocationMutationResult] =
    useGetStoreLocationsMutation();

  React.useEffect(() => {
    // Skip fetching store locations if LocationMapping feature is not available
    if (hasPlatformFeatureAccess(setupType, PlatformFeature.LocationMapping)) {
      var credentials = "";
      if (
        setupType === SetupType.Shopify ||
        setupType == SetupType.BigCommerce
      ) {
        credentials = JSON.stringify({
          storeUrl: shopifyUrl,
          accessToken: shopifyAccessToken,
        });
      } else {
        credentials = siteCredentials;
      }
      storeLocationMutation({
        accessToken: shopifyAccessToken ?? "",
        storeUrl: shopifyUrl ?? "",
        setupType: setupType,
      }).then((res: any) => {
        if (res.data && res.data.status === 200) {
          setStoreLocations(
            res?.data?.entityList?.map((loc: any) => ({
              id: loc.id,
              name: loc.name,
              address: loc.address1,
            })),
          );
        }
      });
    } else {
      return;
    }
  }, []);

  React.useEffect(() => {
    // For setups without LocationMapping, set all locations with storeLocationId as 0
    if (hasPlatformFeatureAccess(setupType, PlatformFeature.LocationMapping)) {
      if (storeLocations && storeLocations.length > 0) {
        let mappedShopifyLocationIds = new Set();
        let preMapLocations = locations
          .filter((loc) => loc.name !== null && loc.address !== null)
          .map((location) => {
            const match = storeLocations.find(
              (loc) =>
                loc?.name?.trim()?.toLowerCase() ===
                  location?.name?.trim()?.toLowerCase() &&
                !mappedShopifyLocationIds.has(loc.id),
            );

            if (match) {
              mappedShopifyLocationIds.add(match.id);
              return { ...location, storeLocationId: match.id };
            } else {
              return { ...location, storeLocationId: 0 };
            }
          });
        setDmsLocations(preMapLocations);
        preMapLocations.map((loc) =>
          loc.storeLocationId === 0
            ? dispatch(removeLocation(loc.dealerId.toString()))
            : loc,
        );
      }
    } else {
      const mappedLocations = locations.map((location) => ({
        ...location,
        storeLocationId: 0,
      }));
      setDmsLocations(mappedLocations);
      return;
    }
  }, [storeLocations, setupType]);

  React.useEffect(() => {
    if (storeLocationMutationResult.isSuccess) {
      setStoreLocations(storeLocationMutationResult.data?.entityList);
    }
  }, [storeLocationMutationResult]);

  React.useEffect(() => {}, []);

  function isLocationExist(item: number) {
    if (hasPlatformFeatureAccess(setupType, PlatformFeature.LocationMapping)) {
      return storeLocations.some((i: any) => i.id === item);
    }
    return true;
  }

  const handleLocationMapChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseInt(event.target.value, 10);
    setSelectedStoreLocationId(value);
  };

  const handleClose = () => {
    setOpenMapper(false);
    setSelectedStoreLocationId(0);
  };

  const handleSaveMapping = () => {
    let updatedLocations = dmsLocations.map((loc) =>
      loc.dealerId === locationToMap?.dealerId
        ? { ...loc, storeLocationId: selectedStoreLocationId }
        : loc,
    );
    setDmsLocations(updatedLocations);
    setSelectedStoreLocationId(0);
    setOpenMapper(false);
  };

  const handleRemoveLocationFromMapping = (
    storeLocationId: number,
    dealerId: number,
  ) => {
    let updatedLocations = dmsLocations.map((loc) =>
      loc.storeLocationId === storeLocationId
        ? { ...loc, storeLocationId: 0 }
        : loc,
    );
    setDmsLocations(updatedLocations);
    dispatch(removeLocation(dealerId.toString()));
  };

  const handleNextPage = () => {
    // For setups without LocationMapping, save all selected locations with storeLocationId as 0
    if (hasPlatformFeatureAccess(setupType, PlatformFeature.LocationMapping)) {
      dispatch(
        addMappedLocations(
          dmsLocations.filter((loc) => loc.storeLocationId > 0),
        ),
      );
    } else {
      const selectedLocations = dmsLocations.filter((loc) =>
        locationIds.some((id: any) => id == loc.dealerId.toString()),
      );
      dispatch(
        addMappedLocations(
          selectedLocations.map((loc) => ({ ...loc, storeLocationId: 1 })),
        ),
      );
    }
    handleNext();
  };
  const handleGetShopifyLocation = (locationId: number) => {
    let shopLocation = storeLocations.find((loc) => loc.id === locationId);
    if (shopLocation) {
      return shopLocation?.name + " (" + shopLocation?.address + ").";
    }
  };

  const handleTimezoneChange = (event: SelectChangeEvent<string>) => {
    dispatch(setTimezone({ timezoneId: event.target.value }));
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: 0.5,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setNavigateOpen(true);
            }}
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleNextPage}
            disabled={locationIds.length === 0 || !timezoneId}
          >
            Next
          </Button>
        </Box>
        <FormControl
          sx={{ m: 3 }}
          component="fieldset"
          variant="standard"
          style={{ alignItems: "center" }}
        >
          <FormControl fullWidth sx={{ mb: 1, maxWidth: 900 }}>
            <FormLabel component="h2" sx={{ mb: 1, fontSize: "1.2rem" }}>
              Timezone
            </FormLabel>
            <Select
              displayEmpty
              value={timezoneId || ""}
              onChange={handleTimezoneChange}
              size="small"
              disabled={isTimezonesLoading}
            >
              <MenuItem value="" disabled>
                {isTimezonesLoading
                  ? "Loading timezones..."
                  : "Select timezone"}
              </MenuItem>
              {timezoneOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormLabel component="h2" style={{ fontSize: "1.5rem" }}>
            Please select locations you want to sync to website
          </FormLabel>
          {!hasMultiLocation && dmsLocations.length > 1 && (
            <Alert severity="warning" sx={{ maxWidth: 900 }}>
              Your subscription does'nt include Multi Location mapping. Please
              upgrade your subscription for multiple locations.
            </Alert>
          )}
          {storeLocationMutationResult.isLoading ? (
            <CircularProgress size="1.2rem" color="inherit" />
          ) : (
            <FormGroup>
              {dmsLocations.map((item, index) => {
                return (
                  <>
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={locationIds.some(
                            (i: any) => i == item.dealerId.toString(),
                          )}
                          onChange={(e) => {
                            console.log(e);
                            if (e.target.checked) {
                              dispatch(addLocation(item.dealerId.toString()));
                            } else {
                              dispatch(
                                removeLocation(item.dealerId.toString()),
                              );
                            }
                          }}
                          name={item?.dmsLocationId.toString()}
                        />
                      }
                      label={
                        <>
                          {item?.name ?? ""}{" "}
                          <Typography component="span" fontSize="small">
                            {item?.address ? "(" + item.address + ")" : null}
                          </Typography>
                        </>
                      }
                      disabled={
                        !isLocationExist(item.storeLocationId) ||
                        (!hasMultiLocation &&
                          locationIds.length > 0 &&
                          !locationIds.some(
                            (i: any) => i == item.dealerId.toString(),
                          ))
                      }
                    />
                    {hasPlatformFeatureAccess(
                      setupType,
                      PlatformFeature.LocationMapping,
                    ) &&
                      (item.storeLocationId > 0 ? (
                        <Typography
                          mt={-1}
                          ml={4}
                          variant="caption"
                          color="green"
                        >
                          {" "}
                          {handleGetShopifyLocation(item.storeLocationId)}
                          <Link
                            sx={{ cursor: "pointer", fontStyle: "italic" }}
                            onClick={() => {
                              handleRemoveLocationFromMapping(
                                item.storeLocationId,
                                item.dealerId,
                              );
                            }}
                          >
                            Remove
                          </Link>
                        </Typography>
                      ) : (
                        <Typography
                          mt={-1}
                          ml={4}
                          variant="caption"
                          color="error"
                        >
                          This location is not mapped to any location of{" "}
                          {setupTypeLabelMap[setupType]}.
                          <Link
                            sx={{
                              cursor: "pointer",
                              fontStyle: "italic",
                              pointerEvents:
                                !hasMultiLocation &&
                                dmsLocations.some(
                                  (l) =>
                                    l.storeLocationId > 0 &&
                                    l.dealerId !== item.dealerId,
                                )
                                  ? "none"
                                  : "auto",
                              opacity:
                                !hasMultiLocation &&
                                dmsLocations.some(
                                  (l) =>
                                    l.storeLocationId > 0 &&
                                    l.dealerId !== item.dealerId,
                                )
                                  ? 0.5
                                  : 1,
                            }}
                            onClick={() => {
                              setLocationToMap(item);
                              setOpenMapper(true);
                            }}
                          >
                            Map now
                          </Link>
                        </Typography>
                      ))}
                  </>
                );
              })}
            </FormGroup>
          )}
        </FormControl>
      </Box>

      <Dialog
        fullWidth
        open={openMapper}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          Location Mapper
        </DialogTitle>
        <DialogContent sx={{ width: "auto" }}>
          <Paper
            elevation={3}
            sx={{ marginTop: 1, padding: "15px", borderRadius: "5px" }}
          >
            <Typography fontSize="small">
              Select any location from the followings to map with selected.
            </Typography>
            <FormControl sx={{ mt: 1 }}>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={selectedStoreLocationId}
                onChange={handleLocationMapChange}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Grid container spacing={2}>
                  {storeLocations.map((loc) => (
                    <Grid item xs={6}>
                      <FormControlLabel
                        value={loc.id}
                        disabled={dmsLocations.some(
                          (location) => location.storeLocationId === loc.id,
                        )}
                        control={<Radio />}
                        label={
                          <>
                            <Typography component="span" fontSize="0.8rem">
                              {loc?.name ?? ""}
                            </Typography>
                            <Typography component="span" fontSize="0.6rem">
                              {loc.address ? " (" + loc?.address + ")" : ""}
                            </Typography>
                          </>
                        }
                        sx={{ fontSize: "0.9rem" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            autoFocus
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              handleSaveMapping();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <AppConfirmDialog
        headerText="Confirm Navigation"
        bodyText="Are you sure you want to go back? You will lose Locations mapping."
        onFail={() => {
          setNavigateOpen(false);
        }}
        onSuccess={() => {
          setNavigateOpen(false);
          handleBack();
        }}
        handleClose={() => {
          setNavigateOpen(false);
        }}
        isOpen={navigateOpen}
      />
    </>
  );
};

export default DmsLocations;
